import PouchDB from 'pouchdb-browser';
import { v4 as uuid } from 'uuid';
import { DocumentType } from './models/document-type.enum';
import {
   type Project,
   type Scene,
   type Character,
   type Location,
   type Plot,
} from '../types/entities';
import AppDispatcher from '../flux/AppDispatcher';
import { ActionTypes } from '../flux/Constants';

type EntityType = DocumentType;
type Entity = Project | Scene | Character | Location | Plot;

let db: PouchDB.Database<any> | null = null;
let remote: PouchDB.Database<any> | null = null;
let currentUserId: string | null = null;
let currentLocalDbName: string | null = null;
const remoteOverrides: Record<string, { url: string; username?: string; password?: string }> = {};
let syncHandler: PouchDB.Replication.Sync<any> | null = null;
let syncBlockedReason: string | null = null;
const attachmentUrlCache = new Map<string, string>();

// Connection timeout in milliseconds
const CONNECTION_TIMEOUT_MS = 5000;

// Online/offline sync management
let onlineListener: (() => void) | null = null;
let offlineListener: (() => void) | null = null;
let syncPausedForOffline = false;

const cleanupSyncListeners = () => {
   if (typeof window === 'undefined') return;
   if (onlineListener) {
      window.removeEventListener('online', onlineListener);
      onlineListener = null;
   }
   if (offlineListener) {
      window.removeEventListener('offline', offlineListener);
      offlineListener = null;
   }
};

const pauseSyncForOffline = () => {
   if (syncHandler && !syncPausedForOffline) {
      console.log('[Sync] Browser went offline, pausing sync');
      syncHandler.cancel();
      syncHandler = null;
      syncPausedForOffline = true;
   }
};

const resumeSyncAfterOnline = async () => {
   if (!syncPausedForOffline || !db || !remote) return;
   console.log('[Sync] Browser back online, validating credentials before sync...');

   // Test connection before resuming sync to avoid account lockout from stale credentials
   try {
      const testPromise = remote.info();
      const timeoutPromise = new Promise((_, reject) =>
         setTimeout(() => reject(new Error('Connection timeout')), CONNECTION_TIMEOUT_MS),
      );
      await Promise.race([testPromise, timeoutPromise]);
      console.log('[Sync] Credentials valid, resuming sync');
      syncPausedForOffline = false;
      startSync();
   } catch (err: any) {
      console.warn('[Sync] Credential validation failed:', err);
      if (err.status === 401 || err.status === 403) {
         // Credentials are invalid - don't attempt sync (would trigger account lockout)
         // Dispatch event to trigger credential refresh
         console.log('[Sync] Auth failed, requesting credential refresh');
         syncPausedForOffline = false;
         remote = null; // Disable sync until credentials are refreshed
         AppDispatcher.dispatch({
            type: ActionTypes.CREDENTIALS_REFRESH_NEEDED,
            reason: 'auth_failed_on_resume',
         });
      } else {
         // Network or other error - try sync anyway, it has its own retry logic
         console.log('[Sync] Non-auth error, attempting sync anyway');
         syncPausedForOffline = false;
         startSync();
      }
   }
};

/**
 * Dispatches sync health updates to the store
 */
const dispatchSyncHealth = (update: {
   lastSuccessAt?: number;
   lastErrorAt?: number;
   lastErrorCode?: string;
   pendingChanges?: number;
   isActive?: boolean;
}) => {
   AppDispatcher.dispatch({
      type: ActionTypes.SYNC_HEALTH_UPDATE,
      health: update,
   });
};

/**
 * Internal function to start sync - used after remote is configured
 */
const startSync = () => {
   if (!db || !remote || syncBlockedReason) return;

   // Cancel any existing sync first
   if (syncHandler) {
      syncHandler.cancel();
      syncHandler = null;
   }

   dispatchSyncHealth({ isActive: true });

   syncHandler = db
      .sync(remote, {
         live: true,
         retry: true,
         batch_size: 1,
         batches_limit: 1,
         back_off_function: (delay: number) => {
            // Exponential backoff with max of 30 seconds
            // Don't retry at all if offline
            if (typeof navigator !== 'undefined' && !navigator.onLine) {
               return 60000; // Long delay when offline (will be cancelled anyway)
            }
            return Math.min(delay * 2, 30000);
         },
      })
      .on('change', (info) => {
         // Track successful sync events
         dispatchSyncHealth({
            lastSuccessAt: Date.now(),
            pendingChanges: info.pending || 0,
         });
      })
      .on('paused', (err) => {
         // Paused - either caught up or offline
         if (err) {
            dispatchSyncHealth({
               lastErrorAt: Date.now(),
               lastErrorCode: String((err as { status?: number })?.status || 'paused'),
               isActive: false,
            });
         } else {
            dispatchSyncHealth({ isActive: false, pendingChanges: 0 });
         }
      })
      .on('active', () => {
         // Sync resumed
         dispatchSyncHealth({ isActive: true });
      })
      .on('error', (err) => {
         console.error('Pouch sync error', { err });
         dispatchSyncHealth({
            lastErrorAt: Date.now(),
            lastErrorCode: String((err as { status?: number })?.status || 'error'),
            isActive: false,
         });

         if ((err as { status?: number })?.status === 413) {
            syncBlockedReason = 'oversized payload (413). Attachments may exceed CouchDB limits.';
            console.error(`Pouch sync halted: ${syncBlockedReason}`);
            syncHandler?.cancel();
            syncHandler = null;

            // Dispatch action to notify UI
            AppDispatcher.dispatch({
               type: ActionTypes.SYNC_BLOCKED,
               reason: 'oversized_payload',
               message:
                  'An attachment is too large to sync. Please remove attachments larger than 10MB.',
            });
         }
      });
};

const injectAuth = (url: string, username?: string, password?: string) => {
   if (!username || !password) return url;
   const encodedPass = encodeURIComponent(password);
   return url.replace('://', `://${encodeURIComponent(username)}:${encodedPass}@`);
};

const extractDbName = (url?: string | null) => {
   if (!url) return null;
   try {
      const parsed = new URL(url);
      const parts = parsed.pathname.split('/').filter(Boolean);
      return parts[parts.length - 1] || null;
   } catch {
      return null;
   }
};

const buildRemoteUrl = (userId: string) => {
   const override = remoteOverrides[userId];
   console.log('[buildRemoteUrl] userId:', userId, 'override:', override);
   if (override?.url) {
      const url = injectAuth(override.url, override.username, override.password);
      console.log('[buildRemoteUrl] Returning URL with auth');
      return url;
   }
   // No override provided; for per-user DBs we require explicit credentials.
   console.log('[buildRemoteUrl] No override found, returning empty');
   return '';
};

const deriveLocalDbName = (userId: string, remoteConfig?: { url?: string }) => {
   const base =
      extractDbName(remoteConfig?.url) ||
      extractDbName(remoteOverrides[userId]?.url) ||
      process.env.REACT_APP_COUCH_DB ||
      'quill-studio';
   return `${base}_${userId}`;
};

const isNetworkError = (err: unknown) => {
   const message = (err as { message?: string })?.message?.toLowerCase() || '';
   const status = (err as { status?: number })?.status;
   return (
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('network error') ||
      status === 0
   );
};

const makeId = (type: EntityType) => `${type}:${Date.now()}:${uuid()}`;
const attachmentCacheKey = (docId: string, attachmentId: string) => `${docId}:${attachmentId}`;

/**
 * Wraps a promise with a timeout
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout in milliseconds
 * @param errorMessage - Error message if timeout occurs
 */
const withTimeout = <T>(
   promise: Promise<T>,
   timeoutMs: number,
   errorMessage: string,
): Promise<T> => {
   return Promise.race([
      promise,
      new Promise<T>((_, reject) => setTimeout(() => reject(new Error(errorMessage)), timeoutMs)),
   ]);
};

export const initPouch = async (
   userId: string,
   remoteConfig?: { url: string; username?: string; password?: string },
) => {
   console.log(
      '[initPouch] Called with userId:',
      userId,
      'remoteConfig:',
      remoteConfig
         ? {
              url: remoteConfig.url,
              hasUsername: !!remoteConfig.username,
              hasPassword: !!remoteConfig.password,
           }
         : 'none',
   );
   if (!userId) {
      throw new Error('initPouch called without a userId');
   }
   const targetLocalName = deriveLocalDbName(userId, remoteConfig);
   if (
      currentUserId === userId &&
      db &&
      currentLocalDbName === targetLocalName &&
      (remote || !remoteConfig?.url)
   ) {
      console.log('[initPouch] Already initialized, returning existing');
      return { db, remote };
   }
   currentUserId = userId;
   currentLocalDbName = targetLocalName;
   db = new PouchDB(targetLocalName);

   if (remoteConfig?.url) {
      console.log('[initPouch] Storing remoteConfig in remoteOverrides');
      remoteOverrides[userId] = remoteConfig;
   }

   const remoteUrl = buildRemoteUrl(userId);
   console.log('[initPouch] Remote URL built:', remoteUrl ? 'Yes' : 'No');
   if (!remoteUrl) {
      throw new Error('Remote CouchDB URL missing. Online workspace setup is required.');
   }
   if (syncBlockedReason) {
      remote = null;
      console.warn(`CouchDB sync disabled: ${syncBlockedReason}`);
      return { db, remote };
   }

   // Test connection before starting sync — only hard-fail on auth errors.
   // Network errors are non-fatal: PouchDB retry handles reconnection automatically.
   if (typeof navigator === 'undefined' || navigator.onLine) {
      try {
         const testDb = new PouchDB(remoteUrl, { skip_setup: false });
         await withTimeout(
            testDb.info(),
            CONNECTION_TIMEOUT_MS,
            'Connection timeout - CouchDB took too long to respond',
         );
         testDb.close();
      } catch (err: any) {
         if (err.status === 401 || err.status === 403) {
            throw new Error(
               `CouchDB authentication failed: ${err.message}. Please contact support to reset your database credentials.`,
            );
         }
         console.warn('[initPouch] Connection test failed, proceeding anyway:', err.message);
      }
   }

   remote = new PouchDB(remoteUrl, { skip_setup: false });
   syncPausedForOffline = false;
   startSync();

   return { db, remote };
};

/**
 * Initializes only the local PouchDB database.
 * Call this immediately after Firebase login to enable offline-first writes.
 * Remote sync should be started separately via startRemoteSync().
 */
export const initLocalDb = async (userId: string): Promise<PouchDB.Database<any>> => {
   console.log('[initLocalDb] Initializing local DB for user:', userId);
   if (!userId) {
      throw new Error('initLocalDb called without a userId');
   }

   const targetLocalName = deriveLocalDbName(userId);

   // If already initialized for this user, return existing
   if (currentUserId === userId && db && currentLocalDbName === targetLocalName) {
      console.log('[initLocalDb] Already initialized, returning existing');
      return db;
   }

   currentUserId = userId;
   currentLocalDbName = targetLocalName;
   db = new PouchDB(targetLocalName);

   console.log('[initLocalDb] Local DB initialized:', targetLocalName);
   return db;
};

/**
 * Starts remote synchronization with CouchDB.
 * Should only be called after workspace is READY (Couch preparation succeeded).
 * This ensures replication is gated behind explicit readiness.
 */
export const startRemoteSync = async (
   userId: string,
   remoteConfig: { url: string; username?: string; password?: string },
): Promise<void> => {
   console.log('[startRemoteSync] Starting remote sync for user:', userId);

   if (!db) {
      // Initialize local DB if not already done
      await initLocalDb(userId);
   }

   if (syncBlockedReason) {
      console.warn('[startRemoteSync] Sync blocked:', syncBlockedReason);
      return;
   }

   // Store remote config for reconnection
   remoteOverrides[userId] = remoteConfig;

   const remoteUrl = buildRemoteUrl(userId);
   if (!remoteUrl) {
      throw new Error('[startRemoteSync] No remote URL available');
   }

   // Test connection before starting sync — only hard-fail on auth errors.
   // Network errors are non-fatal: PouchDB retry handles reconnection automatically.
   if (typeof navigator === 'undefined' || navigator.onLine) {
      try {
         const testDb = new PouchDB(remoteUrl, { skip_setup: false });
         await withTimeout(
            testDb.info(),
            CONNECTION_TIMEOUT_MS,
            'Connection timeout - CouchDB took too long to respond',
         );
         testDb.close();
      } catch (err: any) {
         if (err.status === 401 || err.status === 403) {
            throw new Error(
               `CouchDB authentication failed: ${err.message}. Please contact support to reset your database credentials.`,
            );
         }
         // Network/timeout errors — log and proceed, sync will retry when connection is ready
         console.warn('[startRemoteSync] Connection test failed, proceeding anyway:', err.message);
      }
   }

   remote = new PouchDB(remoteUrl, { skip_setup: false });
   syncPausedForOffline = false;
   startSync();

   // Wire up online/offline event listeners so sync pauses and resumes with the network
   cleanupSyncListeners();
   if (typeof window !== 'undefined') {
      offlineListener = pauseSyncForOffline;
      onlineListener = () => { resumeSyncAfterOnline().catch(console.error); };
      window.addEventListener('offline', offlineListener);
      window.addEventListener('online', onlineListener);
   }
};

/**
 * Stops remote synchronization and cleans up resources.
 * Call this on logout or when transitioning out of READY state.
 */
export const isSyncRunning = (): boolean => syncHandler !== null;

export const stopSync = (): void => {
   console.log('[stopSync] Stopping sync');

   if (syncHandler) {
      syncHandler.cancel();
      syncHandler = null;
   }

   cleanupSyncListeners();
   syncPausedForOffline = false;

   if (remote) {
      remote.close();
      remote = null;
   }

   dispatchSyncHealth({ isActive: false });
};

export const destroyLocalDatabase = async (userId: string): Promise<void> => {
   stopSync();

   if (db && currentUserId === userId) {
      await db.destroy();
      db = null;
   }

   delete remoteOverrides[userId];
   if (currentUserId === userId) {
      currentUserId = null;
      currentLocalDbName = null;
   }
   syncBlockedReason = null;
   clearAttachmentUrlCache();
};

const ensureDb = () => {
   if (!db) throw new Error('PouchDB not initialized. Call initPouch(userId) first.');
   return db;
};

const withUser = <T extends Entity>(data: Partial<T>, userId: string): T => ({
   ...(data as T),
   userId,
   updatedAt: Date.now(),
   createdAt: (data as any)?.createdAt || Date.now(),
});

export const fetchByType = async <T extends Entity>(
   type: EntityType,
   userId: string,
): Promise<T[]> => {
   const database = ensureDb();
   const result = await database.allDocs({ include_docs: true });
   return result.rows
      .map((row) => row.doc as any)
      .filter((doc) => doc?.type === type && doc?.userId === userId)
      .map((doc) => ({ ...doc, id: doc._id })) as T[];
};

export const createDoc = async <T extends Entity>(
   type: EntityType,
   data: Partial<T>,
   userId: string,
): Promise<T> => {
   const database = ensureDb();
   const doc = withUser(data, userId);
   const _id = makeId(type);
   const payload = { ...doc, _id, type };
   await database.put(payload);
   return { ...(payload as any), id: _id };
};

export const updateDocById = async <T extends Entity>(
   id: string,
   updates: Partial<T>,
): Promise<T> => {
   const database = ensureDb();
   let attempt = 0;
   const maxAttempts = 3;

   // Strip PouchDB internals so stale _rev from the caller never overrides the
   // fresh _rev fetched from the database — which would cause permanent 409s on
   // every retry (the second edit failure bug).
   const { _id: _si, _rev: _sr, _deleted: _sd, _attachments: _sa, ...cleanUpdates } = updates as any;

   // Retry on conflict by refetching the latest doc and reapplying updates.
   while (attempt < maxAttempts) {
      const existing = await database.get(id);
      const payload = {
         ...existing,
         ...cleanUpdates,
         updatedAt: Date.now(),
      };

      try {
         const result = await database.put(payload);
         return { ...(payload as any), _rev: result.rev, id };
      } catch (err: any) {
         if (err?.status === 409 || err?.statusCode === 409) {
            attempt += 1;
            continue;
         }
         throw err;
      }
   }
   throw new Error('Failed to update document after retries due to conflicts.');
};

export const deleteDocById = async (id: string): Promise<void> => {
   const database = ensureDb();
   const existing = await database.get(id);
   await database.remove(existing);
};

export const putDocAttachment = async (
   docId: string,
   attachmentId: string,
   data: Blob,
   contentType?: string,
): Promise<void> => {
   const database = ensureDb();
   let attempt = 0;
   const maxAttempts = 3;
   const type = contentType || (data as { type?: string })?.type || 'application/octet-stream';

   while (attempt < maxAttempts) {
      const existing = await database.get(docId);
      try {
         await database.putAttachment(docId, attachmentId, existing._rev, data, type);
         return;
      } catch (err: any) {
         if (err?.status === 409 || err?.statusCode === 409) {
            attempt += 1;
            continue;
         }
         throw err;
      }
   }
   throw new Error('Failed to attach file after retries due to conflicts.');
};

export const getAttachmentUrl = async (
   docId: string,
   attachmentId: string,
): Promise<string | null> => {
   const database = ensureDb();
   if (typeof window === 'undefined') return null;
   const cacheKey = attachmentCacheKey(docId, attachmentId);
   const cached = attachmentUrlCache.get(cacheKey);
   if (cached) return cached;
   const blob = await database.getAttachment(docId, attachmentId);
   if (!blob) return null;
   const url = URL.createObjectURL(blob as Blob);
   attachmentUrlCache.set(cacheKey, url);
   return url;
};

export const getAttachmentBlob = async (
   docId: string,
   attachmentId: string,
): Promise<Blob | null> => {
   const database = ensureDb();
   const blob = await database.getAttachment(docId, attachmentId);
   return (blob as Blob | null) || null;
};

export const revokeAttachmentUrl = (docId: string, attachmentId: string) => {
   if (typeof window === 'undefined') return;
   const cacheKey = attachmentCacheKey(docId, attachmentId);
   const cached = attachmentUrlCache.get(cacheKey);
   if (cached && cached.startsWith('blob:')) {
      URL.revokeObjectURL(cached);
   }
   attachmentUrlCache.delete(cacheKey);
};

export const clearAttachmentUrlCache = () => {
   if (typeof window === 'undefined') return;
   for (const [key, url] of attachmentUrlCache.entries()) {
      if (url.startsWith('blob:')) {
         URL.revokeObjectURL(url);
      }
      attachmentUrlCache.delete(key);
   }
};

/**
 * Resets the sync blocked state and retries synchronization
 * Call this after user has resolved the issue (e.g., removed large attachments)
 */
export const resetSyncBlock = async () => {
   if (!currentUserId || !db) {
      throw new Error('Database not initialized');
   }

   console.log('Resetting sync block and retrying connection');
   syncBlockedReason = null;

   // Cancel existing sync handler if any
   if (syncHandler) {
      syncHandler.cancel();
      syncHandler = null;
   }

   // Retry initialization with current user
   const remoteConfig = remoteOverrides[currentUserId];
   await initPouch(currentUserId, remoteConfig);
};

/**
 * Returns the current sync blocked reason, if any
 */
export const getSyncBlockedReason = (): string | null => {
   return syncBlockedReason;
};
